import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Flex,
  Image,
} from "@chakra-ui/react";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getBrandValue } from "../brandConfig";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef } from "react";
// Icons are loaded from public/icons/ as PNG images
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const NAVY = "#03177A";
const ORANGE = "#FF6600";

interface Service {
  title: string;
  shortDescription: string;
  fullDescription: string;
  benefits: string[];
  highlights: string[];
  iconSrc: string;
}

const services: Service[] = [
  {
    title: "Residential Aged Care",
    shortDescription:
      "Compassionate, personalised care in a homely environment where seniors feel safe, valued, and supported in a community designed to see them flourish.",
    fullDescription:
      "Our residential aged care facilities provide a warm, welcoming home for seniors who need around-the-clock support. We combine clinical excellence with genuine compassion, ensuring every resident receives personalised care that respects their dignity, preferences, and life story. From daily living assistance to specialised dementia and palliative care, our dedicated teams are committed to helping residents truly flourish.",
    benefits: [
      "24/7 registered nursing and personal care",
      "Specialised dementia and memory support programs",
      "Physiotherapy, occupational therapy, and allied health",
      "Nutritious chef-prepared meals tailored to dietary needs",
      "Engaging social activities and community events",
      "Beautiful gardens and purpose-built living spaces",
    ],
    highlights: [
      "Person-Centred Care",
      "24/7 Nursing",
      "Dementia Support",
      "Respite Care",
    ],
    iconSrc: "/icons/VC_Icons_Residential Aged Care.png",
  },
  {
    title: "Home Care",
    shortDescription:
      "Tailored support to help you live independently at home and to enhance your quality of life through compassionate care services and innovative solutions.",
    fullDescription:
      "Our home care services are designed for individuals who wish to remain in the comfort of their own home while receiving professional support. From personal care and domestic assistance to social companionship and transport, we tailor every care plan to meet individual needs and goals. Our experienced carers bring warmth, reliability, and expertise to help you maintain the lifestyle you love.",
    benefits: [
      "Personalised care plans developed with you and your family",
      "Assistance with daily living, meals, and household tasks",
      "Medication management and health monitoring",
      "Social support and community participation",
      "Flexible scheduling to suit your routine",
      "Continuity of care with consistent, familiar carers",
    ],
    highlights: [
      "Home Care Packages",
      "Personal Care",
      "Domestic Assistance",
      "Social Support",
    ],
    iconSrc: "/icons/VC_Icons_Home Care.png",
  },
  {
    title: "NDIS Services",
    shortDescription:
      "Empowering individuals with disabilities to achieve their goals, participate fully in the community, and live vibrant, independent lives.",
    fullDescription:
      "As a registered NDIS provider, Vanguard Care delivers high-quality disability support services that put participants at the centre of everything we do. We work collaboratively with individuals, families, and support coordinators to deliver tailored services that build capacity, foster independence, and enhance quality of life. Our approach is strengths-based, culturally responsive, and focused on meaningful outcomes.",
    benefits: [
      "Supported independent living and accommodation",
      "Community access and social participation programs",
      "Personal care and daily living support",
      "Capacity building and skill development",
      "Support coordination and plan management",
      "Culturally sensitive and inclusive service delivery",
    ],
    highlights: [
      "Registered Provider",
      "Capacity Building",
      "Community Access",
      "Independent Living",
    ],
    iconSrc: "/icons/VC_Icons_NDIS.png",
  },
  {
    title: "Education & Staff Training",
    shortDescription:
      "Comprehensive training to equip staff with the skills and compassion needed to deliver autonomous, client-centred care and support services every day.",
    fullDescription:
      "Vanguard Care is committed to raising the standard of care across the sector through professional education and workforce development. Our training programs are designed by experienced clinicians and educators, covering everything from foundational care skills to advanced clinical practice. We equip care workers with the knowledge, confidence, and compassion to deliver outstanding care every day.",
    benefits: [
      "Accredited training in aged care and disability support",
      "Clinical skills workshops and competency assessments",
      "Dementia care and behavioural support training",
      "Leadership and management development programs",
      "Cultural competency and diversity training",
      "Ongoing professional development and mentoring",
    ],
    highlights: [
      "Accredited Programs",
      "Clinical Skills",
      "Leadership Development",
      "Ongoing Support",
    ],
    iconSrc: "/icons/VC_Icons_Education.png",
  },
];

const CorporateServices = () => {
  const navigate = useNavigate();
  const sectionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (sectionsRef.current) {
      const sections = sectionsRef.current.querySelectorAll(".service-detail");
      gsap.fromTo(
        sections,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionsRef.current,
            start: "top 80%",
          },
        }
      );
    }

    return () => {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <Box bg="#FFFFFF" minHeight="100vh" position="relative">
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        position="relative"
        minHeight={{ base: "55vh", md: "75vh" }}
        display="flex"
        alignItems="center"
        justifyContent="center"
        overflow="hidden"
        bg={NAVY}
        pt={{ base: "110px", md: "145px", lg: "180px" }}
        pb={{ base: "100px", md: "150px", lg: "200px" }}
      >
        {/* Decorative Elements */}
        <Box
          position="absolute"
          top="-20%"
          right="-10%"
          width={{ base: "300px", md: "500px" }}
          height={{ base: "300px", md: "500px" }}
          borderRadius="full"
          bg="rgba(255, 102, 0, 0.1)"
          filter="blur(60px)"
        />
        <Box
          position="absolute"
          bottom="-30%"
          left="-10%"
          width={{ base: "250px", md: "400px" }}
          height={{ base: "250px", md: "400px" }}
          borderRadius="full"
          bg="rgba(255, 255, 255, 0.05)"
          filter="blur(40px)"
        />

        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity={0.03}
          backgroundImage="linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)"
          backgroundSize="50px 50px"
        />

        <Container
          maxW="1200px"
          position="relative"
          zIndex={1}
          px={{ base: 6, md: 8 }}
        >
          <VStack spacing={{ base: 4, md: 6 }} textAlign="center">
            <Box
              display="inline-block"
              px={4}
              py={2}
              bg="rgba(255, 102, 0, 0.2)"
              borderRadius="full"
              mb={2}
            >
              <Text
                fontSize="sm"
                fontWeight="600"
                color={ORANGE}
                letterSpacing="0.1em"
                textTransform="uppercase"
                fontFamily={getBrandValue("fonts.body")}
              >
                Care & Support
              </Text>
            </Box>

            <Heading
              as="h1"
              fontSize={{ base: "3xl", sm: "4xl", md: "5xl", lg: "6xl" }}
              fontWeight="700"
              color="#FFFFFF"
              fontFamily={getBrandValue("fonts.heading")}
              lineHeight="1.1"
            >
              Our Services
            </Heading>

            <Text
              fontSize={{ base: "lg", md: "xl" }}
              color="rgba(255, 255, 255, 0.8)"
              maxW="700px"
              lineHeight="1.7"
              fontFamily={getBrandValue("fonts.body")}
            >
              A full range of care and support services designed to help
              individuals live with dignity, independence, and purpose at every
              stage of life.
            </Text>
          </VStack>
        </Container>

      </Box>

      {/* Service Overview Cards - Navy Section */}
      <Box bg={NAVY} pt={{ base: 4, md: 6 }} pb={{ base: 24, md: 32 }}>
        <Container maxW="1200px" px={{ base: 6, md: 8 }}>
          <VStack spacing={{ base: 6, md: 8 }} textAlign="center">
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 4 }}
              spacing={6}
              mt={{ base: 8, md: 12 }}
              w="100%"
            >
              {services.map((service) => (
                <Box
                  key={service.title}
                  bg="#FFFFFF"
                  borderRadius="xl"
                  p={6}
                  textAlign="left"
                  boxShadow="lg"
                  display="flex"
                  flexDirection="column"
                  transition="all 0.3s ease"
                  _hover={{
                    transform: "translateY(-6px)",
                    boxShadow: "0 15px 40px rgba(0, 0, 0, 0.2)",
                  }}
                >
                  <Box
                    w={14}
                    h={14}
                    borderRadius="full"
                    bg="rgba(255, 102, 0, 0.1)"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={4}
                  >
                    <Image src={service.iconSrc} alt={service.title} w={8} h={8} />
                  </Box>
                  <Heading
                    as="h3"
                    fontSize="lg"
                    fontWeight="600"
                    color={NAVY}
                    fontFamily={getBrandValue("fonts.heading")}
                    mb={3}
                  >
                    {service.title}
                  </Heading>
                  <Text
                    fontSize="sm"
                    color="#555555"
                    lineHeight="1.6"
                    fontFamily={getBrandValue("fonts.body")}
                    mb={4}
                    flex={1}
                  >
                    {service.shortDescription}
                  </Text>
                  <Flex wrap="wrap" gap={2}>
                    {service.highlights.slice(0, 2).map((h) => (
                      <Box
                        key={h}
                        px={3}
                        py={1}
                        bg="rgba(255, 102, 0, 0.1)"
                        borderRadius="full"
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          color={ORANGE}
                          fontFamily={getBrandValue("fonts.body")}
                        >
                          {h}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Detailed Service Sections */}
      <Box ref={sectionsRef}>
        {services.map((service, index) => (
          <Box
            key={service.title}
            className="service-detail"
            py={{ base: 16, md: 24, lg: 32 }}
            bg={index % 2 === 0 ? "#FFFFFF" : "#F8FAFF"}
          >
            <Container maxW="1400px" px={{ base: 6, md: 10, lg: 16 }}>
              <Flex
                direction={{
                  base: "column",
                  lg: index % 2 === 0 ? "row" : "row-reverse",
                }}
                gap={{ base: 8, lg: 20 }}
                align="start"
              >
                {/* Left: Emoji + Highlights */}
                <Box
                  flex="0 0 auto"
                  w={{ base: "100%", lg: "300px" }}
                  textAlign={{ base: "center", lg: "left" }}
                >
                  <Box
                    w={{ base: "80px", md: "100px" }}
                    h={{ base: "80px", md: "100px" }}
                    borderRadius="full"
                    bg={index % 2 === 0 ? NAVY : ORANGE}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mx={{ base: "auto", lg: "0" }}
                    mb={6}
                    boxShadow={`0 10px 40px ${
                      index % 2 === 0
                        ? "rgba(3, 23, 122, 0.2)"
                        : "rgba(255, 102, 0, 0.2)"
                    }`}
                  >
                    <Image src={service.iconSrc} alt={service.title} w={{ base: "50px", md: "60px" }} h={{ base: "50px", md: "60px" }} filter="brightness(0) invert(1)" />
                  </Box>

                  {/* Highlight tags */}
                  <Flex
                    wrap="wrap"
                    gap={2}
                    justify={{ base: "center", lg: "flex-start" }}
                  >
                    {service.highlights.map((h) => (
                      <Box
                        key={h}
                        px={4}
                        py={2}
                        bg={
                          index % 2 === 0
                            ? "rgba(3, 23, 122, 0.08)"
                            : "rgba(255, 102, 0, 0.08)"
                        }
                        borderRadius="full"
                        border="1px solid"
                        borderColor={
                          index % 2 === 0
                            ? "rgba(3, 23, 122, 0.15)"
                            : "rgba(255, 102, 0, 0.15)"
                        }
                      >
                        <Text
                          fontSize="sm"
                          fontWeight="500"
                          color={index % 2 === 0 ? NAVY : ORANGE}
                          fontFamily={getBrandValue("fonts.body")}
                        >
                          {h}
                        </Text>
                      </Box>
                    ))}
                  </Flex>
                </Box>

                {/* Right: Content */}
                <VStack
                  flex="1"
                  align={{ base: "center", lg: "start" }}
                  spacing={6}
                  textAlign={{ base: "center", lg: "left" }}
                >
                  <HStack spacing={3}>
                    <Box w="4px" h="28px" bg={ORANGE} borderRadius="full" />
                    <Heading
                      as="h2"
                      fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
                      fontWeight="700"
                      color={NAVY}
                      fontFamily={getBrandValue("fonts.heading")}
                    >
                      {service.title}
                    </Heading>
                  </HStack>

                  <Text
                    fontSize={{ base: "md", md: "lg" }}
                    color="rgba(3, 23, 122, 0.75)"
                    lineHeight="1.9"
                    fontFamily={getBrandValue("fonts.body")}
                  >
                    {service.fullDescription}
                  </Text>

                  {/* Benefits List */}
                  <VStack align="start" spacing={3} w="100%">
                    <Text
                      fontSize="md"
                      fontWeight="600"
                      color={NAVY}
                      fontFamily={getBrandValue("fonts.heading")}
                    >
                      Key Benefits
                    </Text>
                    <SimpleGrid
                      columns={{ base: 1, md: 2 }}
                      spacing={3}
                      w="100%"
                    >
                      {service.benefits.map((benefit) => (
                        <HStack key={benefit} spacing={3} align="start">
                          <Box
                            w={2}
                            h={2}
                            borderRadius="full"
                            bg={ORANGE}
                            mt={2}
                            flexShrink={0}
                          />
                          <Text
                            fontSize="sm"
                            color="rgba(3, 23, 122, 0.7)"
                            lineHeight="1.6"
                            fontFamily={getBrandValue("fonts.body")}
                          >
                            {benefit}
                          </Text>
                        </HStack>
                      ))}
                    </SimpleGrid>
                  </VStack>

                  {/* CTA */}
                  <Box
                    as="button"
                    position="relative"
                    display="inline-flex"
                    alignItems="center"
                    alignSelf={{ base: "center", lg: "flex-start" }}
                    bg="transparent"
                    pl={0}
                    pr={4}
                    py={2}
                    h="clamp(40px, 3.5vw, 48px)"
                    transition="all 0.3s ease"
                    cursor="pointer"
                    border="none"
                    role="group"
                    _hover={{ transform: "translateY(-2px)" }}
                    onClick={() => {
                      navigate("/contact-us");
                      window.scrollTo(0, 0);
                    }}
                  >
                    <Box
                      position="absolute"
                      left="0"
                      top="50%"
                      transform="translateY(-50%)"
                      w="clamp(30px, 2.5vw, 36px)"
                      h="clamp(30px, 2.5vw, 36px)"
                      borderRadius="full"
                      bg={ORANGE}
                      transition="all 0.4s ease"
                      zIndex={0}
                      _groupHover={{
                        w: "100%",
                        borderRadius: "full",
                      }}
                    />
                    <Text
                      color={NAVY}
                      fontSize="clamp(0.9rem, 1.3vw, 1.1rem)"
                      fontWeight="700"
                      fontFamily={getBrandValue("fonts.body")}
                      position="relative"
                      zIndex={1}
                      pl="clamp(10px, 1vw, 12px)"
                      _groupHover={{ color: "#FFFFFF" }}
                      transition="color 0.4s ease"
                    >
                      Learn More
                    </Text>
                  </Box>
                </VStack>
              </Flex>
            </Container>
          </Box>
        ))}
      </Box>

      {/* CTA Section */}
      <Box
        bg="#F8FAFF"
        py={{ base: 16, md: 20 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="50%"
          left="-100px"
          transform="translateY(-50%)"
          width="200px"
          height="200px"
          borderRadius="full"
          bg="rgba(3, 23, 122, 0.05)"
        />
        <Box
          position="absolute"
          top="50%"
          right="-100px"
          transform="translateY(-50%)"
          width="200px"
          height="200px"
          borderRadius="full"
          bg="rgba(255, 102, 0, 0.05)"
        />

        <Container maxW="800px" position="relative" zIndex={1} px={{ base: 6, md: 8 }}>
          <VStack spacing={6} textAlign="center">
            <Heading
              as="h2"
              fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
              fontWeight="700"
              color={NAVY}
              fontFamily={getBrandValue("fonts.heading")}
            >
              Ready to Get Started?
            </Heading>

            <Text
              fontSize={{ base: "md", md: "lg" }}
              color="rgba(3, 23, 122, 0.7)"
              maxW="600px"
              lineHeight="1.8"
              fontFamily={getBrandValue("fonts.body")}
            >
              Whether you're exploring aged care options, seeking in-home
              support, or looking for NDIS services, our friendly team is here
              to help you find the right care solution.
            </Text>

            <Box
              as="button"
              mt={4}
              px={10}
              py={4}
              bg={ORANGE}
              color="#FFFFFF"
              borderRadius="full"
              fontSize="lg"
              fontWeight="600"
              fontFamily={getBrandValue("fonts.body")}
              border="none"
              cursor="pointer"
              transition="all 0.3s ease"
              _hover={{
                bg: "#E55A00",
                transform: "translateY(-3px)",
                boxShadow: "0 10px 30px rgba(255, 102, 0, 0.3)",
              }}
              _active={{ transform: "translateY(0px)" }}
              onClick={() => {
                navigate("/contact-us");
                window.scrollTo(0, 0);
              }}
            >
              Book a Consultation
            </Box>
          </VStack>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default CorporateServices;
